import { useCallback, useRef, useState } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { CACHE_TTL_MS } from '../constants.tsx';

export type useFetchWithCacheReturn<T> = {
	status: 'idle' | 'loading' | NonNullable<T> | 'error';
	fetch: () => void;
};

export const useFetchWithCache = <T,>(
	callback: () => Promise<T>,
	id: string,
): useFetchWithCacheReturn<T> => {
	const lastFetchedAtRef = useRef<number | null>(null);
	const [status, setStatus] = useState<useFetchWithCacheReturn<T>['status']>('idle');

	const fetch = useCallback(async () => {
		// Skip if cached, not fetching and was successful recently and not stale
		if (lastFetchedAtRef.current && Date.now() - lastFetchedAtRef.current < CACHE_TTL_MS) {
			return;
		}
		setStatus('loading');
		lastFetchedAtRef.current = Date.now();
		try {
			const data = await callback();
			if (data === undefined || data === null) {
				// nothing came back, mark as error and clear cache
				setStatus('error');
				lastFetchedAtRef.current = null;
				return;
			}
			setStatus(data);
		} catch (error) {
			fireErrorAnalytics({
				meta: {
					id,
					packageName: 'jiraShareDialog',
					teamName: 'jira-warepil',
				},
				error: error instanceof Error ? error : new Error(`Unknown error while fetching ${id}`),
			});
			setStatus('error');
			lastFetchedAtRef.current = null;
		}
	}, [callback, id]);

	return { status, fetch };
};
